import { MoreHorizontal, Search, X } from 'lucide-react'
import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useViewOnly } from '@/components/templates/app-shell'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { AddChip } from '@/components/ui/add-chip'
import { Input } from '@/components/ui/input'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { RosterDialog } from '@/pages/OrganizationRosterDialog'
import { deleteJSON } from '@/lib/api'
import type { OrganizationProfile, ProfileMembership, RosterPlace } from '@/lib/contacts/types'
import { displayDate, displayDateTime } from '@/lib/dates'
import { MembershipHistory } from '@/pages/PersonMembershipHistory'
import { membershipRenewalDueLabel } from '@/pages/PersonMembershipDisplay'
import { isConnectionOwned, OwnedBanner, OwnedChip } from '@/components/OwnedChip'

const statusVariants: Record<ProfileMembership['status'], BadgeProps['variant']> = {
  Active: 'success',
  Lapsed: 'warning',
  Suspended: 'neutral',
  'Pending-New': 'warning',
  'Pending-Renewal': 'warning',
}

export function OrganizationMembershipTab({ organization }: { organization: OrganizationProfile }) {
  const viewOnly = useViewOnly()
  const membership = organization.membership

  if (!membership) {
    return (
      <Card className="max-w-2xl">
        <CardContent className="p-0">
          <EmptyState
            title="No membership"
            className="min-h-44"
            action={
              viewOnly ? undefined : (
                <Button
                  type="button"
                  variant="outline"
                  className="h-auto border-dashed px-1.5 py-0.5 text-micro"
                >
                  + Assign membership
                </Button>
              )
            }
          />
        </CardContent>
      </Card>
    )
  }

  const canRenew = membership.status === 'Lapsed' && !viewOnly

  return (
    <div className="flex w-full flex-col gap-4">
      <OwnedBanner
        owner={membership.owner}
        externalUrl={membership.external_url}
        lastSyncedAt={membership.last_synced_at}
      />
      <Card>
        <CardHeader className="gap-3 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-body font-semibold text-foreground">
                  {membership.level}
                </CardTitle>
                <Badge variant={statusVariants[membership.status]}>{membership.status}</Badge>
                <OwnedChip owner={membership.owner} />
              </div>
              <p className="pt-1 text-body-sm text-muted-foreground">
                {membershipRenewalSummary(membership)}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {canRenew && !isConnectionOwned(membership.owner) && (
                <Button type="button" size="sm">
                  Renew
                </Button>
              )}
              {!viewOnly && !isConnectionOwned(membership.owner) && <ManageMembershipMenu />}
            </div>
          </div>
        </CardHeader>
        <CardContent className="divide-y divide-border p-0">
          <section className="px-4 pb-4">
            <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
              <Field label="Member number" value={memberNumberLabel(membership)} />
              <Field label="Member since" value={displayDate(membership.member_since_on)} />
              <Field
                label="Renewal due"
                value={membershipRenewalDueLabel(membership.renewal_due_on)}
              />
              <Field label="Roster" value={rosterSummary(membership)} />
            </dl>
          </section>
          <RosterSection organization={organization} membership={membership} viewOnly={viewOnly} />
          <MembershipHistory history={organization.membership_history ?? []} autopayHistory={[]} />
        </CardContent>
      </Card>
    </div>
  )
}

function rosterSummary(membership: ProfileMembership) {
  const count = membership.roster_count ?? membership.roster_places?.length ?? 0
  return membership.roster_cap == null ? String(count) : `${count} of ${membership.roster_cap}`
}

function RosterSection({
  organization,
  membership,
  viewOnly,
}: {
  organization: OrganizationProfile
  membership: ProfileMembership
  viewOnly: boolean
}) {
  const queryClient = useQueryClient()
  const [search, setSearch] = React.useState('')
  const [sort, setSort] = React.useState<'person' | 'added'>('person')
  const [descending, setDescending] = React.useState(false)
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [places, setPlaces] = React.useState<RosterPlace[]>(membership.roster_places ?? [])
  const roster = places
    .filter((place) => {
      const query = search.toLowerCase()
      return (
        place.name.toLowerCase().includes(query) ||
        (place.email ?? '').toLowerCase().includes(query)
      )
    })
    .sort((a, b) => {
      const result =
        sort === 'person' ? a.name.localeCompare(b.name) : a.created_at.localeCompare(b.created_at)
      return descending ? -result : result
    })
  const remove = useMutation({
    mutationFn: (id: number) =>
      deleteJSON(
        `/api/v1/organizations/${organization.id}/memberships/${membership.id}/roster_places/${id}`,
      ),
    onSuccess: (_data, id) => {
      setPlaces((current) => current.filter((place) => place.id !== id))
      queryClient.invalidateQueries({ queryKey: ['organization-profile', organization.id] })
    },
  })
  const toggleSort = (next: 'person' | 'added') => {
    if (sort === next) setDescending((current) => !current)
    else {
      setSort(next)
      setDescending(false)
    }
  }
  const atCap =
    membership.roster_cap != null &&
    Math.max(places.length, membership.roster_count ?? 0) >= membership.roster_cap
  return (
    <section>
      <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-2">
        <h3 className="text-body font-medium">
          Roster · {rosterSummary({ ...membership, roster_count: places.length })}
        </h3>
        {atCap ? (
          <span className="text-caption text-muted-foreground">Roster is full</span>
        ) : viewOnly ? null : (
          <AddChip onClick={() => setDialogOpen(true)}>Add to roster</AddChip>
        )}
      </div>
      <div className="flex items-center gap-2 px-4 pb-3">
        <div className="relative w-64">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            className="h-9 pl-8"
            placeholder="Search name or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>
      <RosterTable
        roster={roster}
        viewOnly={viewOnly}
        sort={sort}
        descending={descending}
        toggleSort={toggleSort}
        onRemove={(id) => remove.mutate(id)}
      />
      <RosterDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        organization={organization}
        membership={membership}
        onAdded={(place) => setPlaces((current) => [...current, place])}
      />
    </section>
  )
}

function RosterTable({
  roster,
  viewOnly,
  sort,
  descending,
  toggleSort,
  onRemove,
}: {
  roster: RosterPlace[]
  viewOnly: boolean
  sort: 'person' | 'added'
  descending: boolean
  toggleSort: (sort: 'person' | 'added') => void
  onRemove: (id: number) => void
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTableHead
            active={sort === 'person'}
            direction={descending ? 'descending' : 'ascending'}
            onToggle={() => toggleSort('person')}
          >
            Person
          </SortableTableHead>
          <TableHead>Email</TableHead>
          <SortableTableHead
            active={sort === 'added'}
            direction={descending ? 'descending' : 'ascending'}
            onToggle={() => toggleSort('added')}
          >
            Added
          </SortableTableHead>
          {!viewOnly && <TableHead className="w-12" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {roster.map((place) => (
          <TableRow key={place.id}>
            <TableCell className="py-2">
              {place.person_id ? (
                <a
                  className="text-primary hover:underline"
                  href={`/contacts/persons/${place.person_id}`}
                >
                  {place.name}
                </a>
              ) : (
                <Badge variant="neutral">Not linked</Badge>
              )}
            </TableCell>
            <TableCell className="py-2 text-muted-foreground">{place.email ?? '-'}</TableCell>
            <TableCell className="py-2 text-muted-foreground">
              {displayDateTime(place.created_at)}
            </TableCell>
            {!viewOnly && (
              <TableCell className="py-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${place.name} from roster`}
                  onClick={() => onRemove(place.id)}
                >
                  <X className="size-4" aria-hidden="true" />
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-caption uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-body-sm text-foreground">{value}</dd>
    </div>
  )
}

function ManageMembershipMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-2">
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
          Manage membership
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuLabel>Change</DropdownMenuLabel>
        <DropdownMenuItem>Change level</DropdownMenuItem>
        <DropdownMenuItem>Correct dates</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Status</DropdownMenuLabel>
        <DropdownMenuItem>Lapse</DropdownMenuItem>
        <DropdownMenuItem destructive>Suspend</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Danger</DropdownMenuLabel>
        <DropdownMenuItem destructive>Delete membership (created in error)</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function memberNumberLabel(membership: ProfileMembership) {
  return membership.member_number ? `#${membership.member_number}` : '-'
}

function membershipRenewalSummary(membership: ProfileMembership) {
  const renewalDue = membershipRenewalDueLabel(membership.renewal_due_on)
  return renewalDue === 'Never' ? 'never expires' : `renews ${renewalDue}`
}
